import React, { useState } from "react"
import { Comment as CommentType, LexicalText } from "@/schema"
import { Alert, AlertDescription } from "@ui/shad"
import { cn } from "@ui/utils"
import { AlertCircleIcon } from "lucide-react"

import { Comment } from "./comment"
import { CommentComposer } from "./comment-composer"

interface CommentListProps {
  comments?: CommentType[]
  onCreateComment: (content: LexicalText, files: File[], parentId?: string) => Promise<void>
  onReaction: (commentId: string, emoji: string) => void
  currentUserId: string
  isLoading?: boolean
  error?: Error | null
  className?: string
  panel?: boolean
  allowMentions?: boolean
  showMicButton?: boolean
  placeholder?: string
  submitLabel?: string
  emptyMessage?: string
  leadingContent?: React.ReactNode
  appearance?: "card" | "thread"
  showTaskLink?: boolean
}

export function CommentList({
  comments = [],
  onCreateComment,
  onReaction,
  currentUserId,
  isLoading = false,
  error = null,
  className,
  panel = false,
  allowMentions = true,
  showMicButton = true,
  placeholder,
  submitLabel,
  emptyMessage,
  leadingContent,
  appearance = "card",
  showTaskLink = false,
}: CommentListProps) {
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [composerResetVersion, setComposerResetVersion] = useState(0)

  const handleCreateComment = async (content: LexicalText, files: File[], parentId?: string) => {
    setIsSubmitting(true)
    try {
      await onCreateComment(content, files, parentId)
      setComposerResetVersion((version) => version + 1)
      setReplyToCommentId(null)
    } catch {
      // Errors are handled by the global MutationCache
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReply = (commentId: string) => {
    setReplyToCommentId(commentId === replyToCommentId ? null : commentId)
  }

  const rootComments = comments.filter((comment) => !comment.parent_id)

  if (isLoading && !leadingContent) {
    return null
  }

  if (error) {
    return (
      <div>
        {leadingContent}
        <Alert variant="destructive">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertDescription>Failed to load comments. Please try again later.</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className={cn(panel ? "flex min-h-0 flex-1 flex-col" : "space-y-4", className)}>
      <div
        className={cn(
          panel ? "bg-background order-last shrink-0 border-t p-3" : "mb-2",
          panel && appearance === "thread" && "px-6"
        )}
      >
        <CommentComposer
          onSubmit={(content, files) => handleCreateComment(content, files)}
          isSubmitting={isSubmitting}
          resetVersion={composerResetVersion}
          allowMentions={allowMentions}
          showMicButton={showMicButton}
          placeholder={placeholder}
          submitLabel={submitLabel}
        />
      </div>
      <div
        className={cn(
          "space-y-3",
          panel && "min-h-0 flex-1 overflow-auto p-4",
          appearance === "thread" && "space-y-4 px-6 py-[18px]"
        )}
      >
        {leadingContent}
        {!isLoading && !rootComments.length && emptyMessage && (
          <p className="text-muted-foreground py-4 text-center text-xs">{emptyMessage}</p>
        )}
        {rootComments.map((comment) => (
          <Comment
            key={comment.id}
            comment={comment}
            onReply={handleReply}
            onReaction={onReaction}
            currentUserId={currentUserId}
            isReplyFormOpen={replyToCommentId === comment.id}
            allowMentions={allowMentions}
            appearance={appearance}
            showTaskLink={showTaskLink}
          />
        ))}
      </div>
    </div>
  )
}
